import { useEffect, useMemo, useState, useRef, useCallback } from 'react';
import { skipToken } from '@reduxjs/toolkit/query';
import { useLanguage } from '../../../../contexts/LanguageContext';
import { useGetPreAccountingListQuery, useGetPreAccountingSelectedTypesQuery, useAddPreAccountingMutation } from '../../../../api/endpoints/preAccounting';
import { useGetRetailDealersQuery } from '../../../../api/endpoints/parts';
import { usePreAccountingCustomerOptions } from '../../../../hooks/usePreAccountingCustomerOptions';
import { InputText } from 'primereact/inputtext';

export default function usePreAcoounting() {
    const { th, getCurrentLanguageId } = useLanguage();
    const langId = getCurrentLanguageId();
    const [selectedCustomerId, setSelectedCustomerId] = useState(null);
    const [selectedCustomerOption, setSelectedCustomerOption] = useState(null);
    const [customerType, setCustomerType] = useState(0);

    const [customerSearchText, setCustomerSearchText] = useState('');
    const [dealerSearchText, setDealerSearchText] = useState('');
    const searchDebounceRef = useRef(null);

    const [dropdownFilterValue, setDropdownFilterValue] = useState('');
    const dropdownDebounceRef = useRef(null);

    const prevDealerOptionsRef = useRef([]);

    const [lazyParams, setLazyParams] = useState({
        first: 0,
        rows: 10,
        page: 1,
        sortField: null,
        sortOrder: null
    });

    const [prepended, setPrepended] = useState([]);

    // Customer dropdown - Custom hook ile (Cache mekanizması dahil)
    const { customerOptions, isFetching: fetchingCustomers } = usePreAccountingCustomerOptions({
        SearchText: customerSearchText,
        RowCount: 50
    });

    const { data: dealersData, isFetching: fetchingDealers } = useGetRetailDealersQuery({
        SearchText: dealerSearchText,
        RowCount: 50
    });
    const { data: selectedTypes, isFetching: loadingTypes } = useGetPreAccountingSelectedTypesQuery({ LanguageId: typeof langId === 'number' ? langId : 2 });

    const dealerOptions = useMemo(() => {
        if (!dealersData) return prevDealerOptionsRef.current;

        const mapped = dealersData
            .map((d) => {
                const label = d.name || d.dealerName || d.label || 'İsimsiz';
                const value = d.id || d.dealerId || d.value;
                return {
                    label: String(label),
                    value: value != null ? Number(value) : null
                };
            })
            .filter((opt) => opt.value != null);

        if (mapped.length > 0) {
            prevDealerOptionsRef.current = mapped;
        }

        return mapped;
    }, [dealersData]);

    const noResultsFound = useMemo(() => {
        const hasFilter = dropdownFilterValue.trim() !== '';
        if (!hasFilter) return false;

        if (customerType === 0) {
            return !fetchingCustomers && customerOptions && customerOptions.length === 0;
        } else {
            return !fetchingDealers && dealersData && dealersData.length === 0;
        }
    }, [customerType, customerOptions, dealersData, dropdownFilterValue, fetchingCustomers, fetchingDealers]);

    const currentOptions = useMemo(() => {
        const opts = customerType === 0 ? customerOptions : dealerOptions;
        const prevOpts = customerType === 0 ? [] : prevDealerOptionsRef.current;

        // Customer için cache hook tarafından yönetiliyor, sadece dealer için kontrol et
        if (customerType === 1 && opts.length === 0 && prevOpts.length > 0) {
            return prevOpts;
        }
        if (selectedCustomerId != null && selectedCustomerOption) {
            const exists = opts.some((opt) => Number(opt.value) === Number(selectedCustomerOption.value));
            if (!exists) {
                return [selectedCustomerOption, ...opts];
            }
        }
        return opts;
    }, [customerType, customerOptions, dealerOptions, selectedCustomerId, selectedCustomerOption]);

    const loadingDropdown = customerType === 0 ? fetchingCustomers : fetchingDealers;

    const addDialogOptions = useMemo(
        () => ({
            paymentTypes: selectedTypes?.paymentTypes ?? [],
            transactionTypes: selectedTypes?.transactionTypes ?? [],
            typeOfMovements: selectedTypes?.typeOfMovements ?? [],
            currencies: selectedTypes?.currencies ?? []
        }),
        [selectedTypes]
    );

    const listArgs = selectedCustomerId
        ? {
              DealerId: selectedCustomerId,
              PageNumber: lazyParams.page,
              PageSize: lazyParams.rows,
              LanguageId: typeof langId === 'number' ? langId : 2,
              CustomerType: customerType
          }
        : skipToken;

    const { data: resp, isFetching } = useGetPreAccountingListQuery(listArgs);

    const activeFilters = useMemo(() => {
        return {
            DealerId: selectedCustomerId ?? undefined,
            LanguageId: typeof langId === 'number' ? langId : 2
        };
    }, [selectedCustomerId, langId]);

    const rawItems = resp?.data?.data?.items ?? [];
    const totalCountApi = resp?.data?.data?.totalCount ?? 0;
    const totalBalances = resp?.data?.totalBalances ?? [];

    const sortedData = useMemo(() => {
        if (!lazyParams.sortField) return rawItems;
        const order = lazyParams.sortOrder === 1 ? 1 : -1;
        return [...rawItems].sort((a, b) => {
            const av = a?.[lazyParams.sortField] ?? '';
            const bv = b?.[lazyParams.sortField] ?? '';
            return order * av.toString().localeCompare(bv.toString(), 'tr', { numeric: true });
        });
    }, [rawItems, lazyParams.sortField, lazyParams.sortOrder]);

    const normId = (v) => (v == null ? null : Number(v));
    const apiIds = useMemo(() => new Set(sortedData.map((x) => normId(x.id))), [sortedData]);
    const newOnly = useMemo(() => prepended.filter((x) => !apiIds.has(normId(x.id))), [prepended, apiIds]);
    const displayData = useMemo(() => {
        const left = newOnly.map((x) => ({ ...x, id: normId(x.id) }));
        const right = sortedData.map((x) => ({ ...x, id: normId(x.id) }));
        return [...left, ...right];
    }, [newOnly, sortedData]);

    const totalRecords = selectedCustomerId ? totalCountApi + newOnly.length : 0;

    useEffect(() => {
        return () => {
            if (dropdownDebounceRef.current) clearTimeout(dropdownDebounceRef.current);
        };
    }, []);

    const handleDropdownFilterInputChange = useCallback(
        (val) => {
            setDropdownFilterValue(val);

            if (dropdownDebounceRef.current) clearTimeout(dropdownDebounceRef.current);

            dropdownDebounceRef.current = setTimeout(() => {
                const trimmed = (val ?? '').trim();
                if (customerType === 0) setCustomerSearchText(trimmed);
                else setDealerSearchText(trimmed);
            }, 400);
        },
        [customerType]
    );

    const dropdownFilterTemplate = useCallback(
        () => (
            <div className="p-dropdown-filter-container" onMouseDown={(e) => e.stopPropagation()}>
                <InputText
                    value={dropdownFilterValue}
                    onChange={(e) => handleDropdownFilterInputChange(e.target.value)}
                    className="p-dropdown-filter w-full"
                    placeholder={th('common.search') || 'Ara'}
                    autoFocus
                    onKeyDown={(e) => e.stopPropagation()}
                    onKeyUp={(e) => e.stopPropagation()}
                    onClick={(e) => e.stopPropagation()}
                />
                <span className="p-dropdown-filter-icon pi pi-search" />
            </div>
        ),
        [dropdownFilterValue, handleDropdownFilterInputChange, th]
    );

    const handleDropdownChange = useCallback((value) => {
        setDropdownFilterValue(''); // ✅ önemli: seçim sonrası filtre text'i temizle
        setCustomerSearchText('');
        setDealerSearchText('');

        if (value) {
            const opts = customerType === 0 ? customerOptions : dealerOptions;
            const found = opts.find((opt) => Number(opt.value) === Number(value));
            setSelectedCustomerOption(
                found || {
                    label: `${value}`,
                    value: Number(value)
                }
            );
            setSelectedCustomerId(value);
            setLazyParams((p) => ({ ...p, first: 0, page: 1 }));
            setPrepended([]);
        } else {
            setSelectedCustomerId(null);
            setSelectedCustomerOption(null);
        }
    }, [customerType, customerOptions, dealerOptions]);

    const handleDropdownShow = useCallback(() => {}, []);

    useEffect(() => {
        return () => {
            if (searchDebounceRef.current) {
                clearTimeout(searchDebounceRef.current);
            }
        };
    }, []);

    const primeFilterLocale = useMemo(() => {
        if (langId === 2) return 'tr';
        if (langId === 1) return 'en';
        if (langId === 3) return 'de';
        if (langId === 4) return 'fr';
        if (langId === 5) return 'it';
        if (langId === 6) return 'ro';
        return 'en';
    }, [langId]);

    const customerDropdown = useMemo(() => {
        const isCustomer = customerType === 0;
        return {
            key: customerType,
            value: selectedCustomerId,
            options: currentOptions,
            placeholder: isCustomer ? th('preAccounting.selectCustomer') : th('preAccounting.selectDealer'),
            loading: loadingDropdown,
            disabled: false,
            filterTemplate: dropdownFilterTemplate,
            onShow: handleDropdownShow,
            onChange: handleDropdownChange,
            emptyMessage: th('common.noRecords') || 'Kayıt bulunamadı'
        };
    }, [customerType, selectedCustomerId, currentOptions, th, loadingDropdown, dropdownFilterTemplate, handleDropdownShow, handleDropdownChange]);

    const onPage = (e) => {
        const page = Math.floor(e.first / e.rows) + 1;
        setLazyParams((p) => ({ ...p, first: e.first, rows: e.rows, page }));
    };
    const onSort = (e) => {
        setLazyParams((p) => ({ ...p, sortField: e.sortField, sortOrder: e.sortOrder }));
    };
    const handleSearch = (val) => {
        setSelectedCustomerId(val || null);
        setLazyParams((p) => ({ ...p, first: 0, page: 1 }));
        setPrepended([]);
    };
    const handleAdvanced = (u) => {
        setSelectedCustomerId(u?.customer ?? null);
        setLazyParams((p) => ({ ...p, first: 0, page: 1 }));
        setPrepended([]);
    };
    const handleReset = () => {
        setSelectedCustomerId(null);
        setSelectedCustomerOption(null);
        setDropdownFilterValue('');
        setCustomerSearchText('');
        setDealerSearchText('');
        setLazyParams({ first: 0, rows: 10, page: 1, sortField: null, sortOrder: null });
        setPrepended([]);
    };

    const handleCustomerTypeChange = (value) => {
        setCustomerType(value);
        setSelectedCustomerId(null);
        setSelectedCustomerOption(null);

        setDropdownFilterValue('');
        setCustomerSearchText('');
        setDealerSearchText('');

        setLazyParams((p) => ({ ...p, first: 0, page: 1 }));
        setPrepended([]);
    };

    useEffect(() => {
        setLazyParams((p) => ({ ...p, first: 0, page: 1 }));
    }, [selectedCustomerId]);

    useEffect(() => {
        if (selectedCustomerId == null) return;
        const opts = customerType === 0 ? customerOptions : dealerOptions;
        const found = opts.find((opt) => Number(opt.value) === Number(selectedCustomerId));
        if (found) {
            setSelectedCustomerOption(found);
        }
    }, [customerType, customerOptions, dealerOptions, selectedCustomerId]);

    const showHeaderBar = !!selectedCustomerId && !isFetching;

    const [addVisible, setAddVisible] = useState(false);
    const [isEditMode, setIsEditMode] = useState(false);

    const getEmptyForm = (dealerId = null) => ({
        id: null,
        dealerId,
        paymentType: null,
        transactionType: null,
        processType: null,
        currency: null,
        amount: null,
        transactionDate: null,
        dueDate: null,
        documentNo: '',
        documentSeriesNo: '',
        description: '',
        createdUser:''
    });

    const [form, setForm] = useState(getEmptyForm());

    const [addPreAccounting, { isLoading: adding }] = useAddPreAccountingMutation();

    const openAdd = () => {
        if (!selectedCustomerId) return;
        setIsEditMode(false);
        setForm(getEmptyForm(selectedCustomerId));
        setAddVisible(true);
    };

    const openEdit = (rowData) => {
        if (!rowData) return;
        setIsEditMode(true);
        const findOptionByLabel = (options, label) => {
            if (!options || !label) return null;
            const found = options.find((opt) => opt.label === label || opt.value === label);
            return found?.value ?? null;
        };
        setForm({
            id: rowData.id ?? null,
            dealerId: rowData.dealerId ?? selectedCustomerId,
            paymentType: findOptionByLabel(addDialogOptions.paymentTypes, rowData.paymentType),
            transactionType: findOptionByLabel(addDialogOptions.transactionTypes, rowData.transactionType),
            processType: findOptionByLabel(addDialogOptions.typeOfMovements, rowData.processType),
            currency: rowData.currency ?? null,
            amount: rowData.amount ?? null,
            transactionDate: rowData.transactionDate ? new Date(rowData.transactionDate) : null,
            dueDate: rowData.dueDate ? new Date(rowData.dueDate) : null,
            documentNo: rowData.documentNo ?? '',
            documentSeriesNo: rowData.documentSeriesNo ?? '',
            description: rowData.description ?? ''
        });
        setAddVisible(true);
    };

    const closeAdd = () => {
        setAddVisible(false);
        setIsEditMode(false);
        setForm(getEmptyForm(selectedCustomerId));
    };

    const saveAdd = async () => {
        const payload = {
            id: isEditMode && form.id ? Number(form.id) : null,
            dealerId: form.dealerId != null ? Number(form.dealerId) : null,
            paymentType: form.paymentType != null ? Number(form.paymentType) : null,
            transactionType: form.transactionType != null ? Number(form.transactionType) : null,
            processType: form.processType != null ? Number(form.processType) : null,
            transactionDate: form.transactionDate ? new Date(form.transactionDate).toISOString() : null,
            dueDate: form.dueDate ? new Date(form.dueDate).toISOString() : null,
            currency: form.currency || null,
            amount: form.amount != null ? Number(form.amount) : null,
            documentNo: form.documentNo?.trim() || null,
            documentSeriesNo: form.documentSeriesNo?.trim() || null,
            description: form.description?.trim() || null,
            languageId: typeof langId === 'number' ? langId : 2
        };

        const res = await addPreAccounting(payload)
            .unwrap()
            .catch(() => null);
        const ok = res?.httpStatusCode === 200 || res?.isSuccessful === true;
        if (ok) {
            if (isEditMode) {
                setPrepended([]);
            } else {
                const rawNew = res?.data ?? null;
                const newRow = rawNew ? { ...rawNew, id: Number(rawNew.id) } : null;

                if (newRow?.id != null) {
                    setPrepended((p) => [newRow, ...p]);
                } else {
                    const temp = { id: Date.now(), ...(rawNew ?? payload) };
                    setPrepended((p) => [temp, ...p]);
                }
            }
            closeAdd();
        }
    };

    return {
        activeFilters,
        customerDropdown,
        handleSearch,
        handleAdvanced,
        handleReset,
        customerType,
        setCustomerType: handleCustomerTypeChange,

        showHeaderBar,
        addEnabled: !!selectedCustomerId && !adding && !loadingTypes,
        totalBalances,

        tableProps: {
            data: selectedCustomerId ? displayData : [],
            totalRecords: selectedCustomerId ? totalRecords : 0,
            lazyParams,
            onPage,
            onSort,
            loading: !!selectedCustomerId && isFetching
        },

        addDialog: {
            visible: addVisible,
            open: openAdd,
            openEdit: openEdit,
            close: closeAdd,
            form,
            setForm,
            save: saveAdd,
            options: addDialogOptions,
            loading: loadingTypes,
            adding,
            isEditMode
        }
    };
}
